'use client';

import { useState, useRef } from 'react';
import { MapPin, Check, Search, AlertCircle } from 'lucide-react';
import { US_STATE_PATHS } from './usStatePaths';

interface ServiceAreasClientProps {
  coveredStates: { name: string; zip_count: number }[];
}

function USMap({
  coveredStateNames,
  coveredStateMap,
  onHover,
  onLeave,
  onClickState,
}: {
  coveredStateNames: Set<string>;
  coveredStateMap: Map<string, number>;
  onHover: (name: string, zipCount: number | undefined, e: React.MouseEvent) => void;
  onLeave: () => void;
  onClickState: (name: string) => void;
}) {
  return (
    <svg viewBox="0 0 980 600" className="w-full h-auto">
      {Object.entries(US_STATE_PATHS).map(([name, path]) => {
        const isCovered = coveredStateNames.has(name);
        const zipCount = coveredStateMap.get(name);
        return (
          <path
            key={name}
            d={path}
            fill={isCovered ? '#10b981' : '#e5e7eb'}
            stroke="#fff"
            strokeWidth={0.75}
            className="transition-colors duration-150 cursor-pointer"
            onMouseEnter={(e) => onHover(name, zipCount, e)}
            onMouseLeave={onLeave}
            onClick={() => onClickState(name)}
            style={{ outline: 'none' }}
          />
        );
      })}
    </svg>
  );
}

export default function ServiceAreasClient({ coveredStates }: ServiceAreasClientProps) {
  const [tooltip, setTooltip] = useState<{ name: string; zipCount?: number; x: number; y: number } | null>(null);
  const [zipCode, setZipCode] = useState('');
  const [checkResult, setCheckResult] = useState<{ covered: boolean; service_area?: string } | null>(null);
  const [checking, setChecking] = useState(false);
  const [zipError, setZipError] = useState('');
  const statesListRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const coveredStateNames = new Set(coveredStates.map(s => s.name));
  const coveredStateMap = new Map(coveredStates.map(s => [s.name, s.zip_count]));

  const handleHover = (name: string, zipCount: number | undefined, e: React.MouseEvent) => {
    const rect = mapContainerRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltip({
        name,
        zipCount,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 15,
      });
    }
  };

  const handleLeave = () => setTooltip(null);

  const handleClickState = (name: string) => {
    if (coveredStateNames.has(name) && statesListRef.current) {
      statesListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCheckZip = async () => {
    const cleaned = zipCode.replace(/\D/g, '');
    if (cleaned.length !== 5) {
      setZipError('Please enter a valid 5-digit zip code.');
      setCheckResult(null);
      return;
    }
    setZipError('');
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await fetch(`/api/v2/commerce/installation/check-zip?zip_code=${cleaned}`);
      const data = await res.json();
      setCheckResult(data);
    } catch {
      setZipError('Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div>
      {/* Interactive US Map */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Installation Coverage Map
          </h2>
          <p className="text-lg text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            Green states have professional installation available. Gray states enjoy free shipping with DIY installation.
          </p>
          <div
            ref={mapContainerRef}
            className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg p-4 md:p-8 relative"
          >
            <USMap
              coveredStateNames={coveredStateNames}
              coveredStateMap={coveredStateMap}
              onHover={handleHover}
              onLeave={handleLeave}
              onClickState={handleClickState}
            />

            {tooltip && (
              <div
                className="absolute pointer-events-none bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg z-10 -translate-x-1/2 -translate-y-full"
                style={{ left: tooltip.x, top: tooltip.y }}
              >
                <div className="font-semibold">{tooltip.name}</div>
                <div className="text-gray-300 text-xs">
                  {tooltip.zipCount
                    ? `${tooltip.zipCount} zip codes covered`
                    : 'Coming Soon — Free Shipping Available'}
                </div>
              </div>
            )}

            <div className="flex items-center gap-6 justify-center mt-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-emerald-500 inline-block" />
                Installation Available
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-gray-200 inline-block border border-gray-300" />
                Free Shipping Only
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zip Code Checker */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Search className="w-6 h-6 text-red-600" />
              <h2 className="text-2xl md:text-3xl font-bold">Check Your Zip Code</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Enter your zip code to see if professional installation is available in your area.
            </p>
            <div className="flex gap-3 max-w-md mx-auto">
              <input
                type="text"
                value={zipCode}
                onChange={(e) => {
                  setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5));
                  setCheckResult(null);
                  setZipError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleCheckZip()}
                placeholder="Enter zip code"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                maxLength={5}
              />
              <button
                onClick={handleCheckZip}
                disabled={checking}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {checking ? 'Checking...' : 'Check Coverage'}
              </button>
            </div>

            {zipError && (
              <div className="mt-4 flex items-center justify-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                <span>{zipError}</span>
              </div>
            )}

            {checkResult && (
              <div className={`mt-6 p-5 rounded-xl ${checkResult.covered ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                {checkResult.covered ? (
                  <div className="flex items-start gap-3 text-left">
                    <Check className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-emerald-800 text-lg">Professional installation available!</p>
                      {checkResult.service_area && (
                        <p className="text-emerald-700 mt-1">Service area: {checkResult.service_area}</p>
                      )}
                      <a
                        href="/products"
                        className="inline-block mt-3 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Shop Now
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 text-left">
                    <MapPin className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800 text-lg">Professional installation coming soon to your area.</p>
                      <p className="text-amber-700 mt-1">Free shipping + DIY installation available now! Our blinds come with easy-to-follow instructions.</p>
                      <a
                        href="/products"
                        className="inline-block mt-3 bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Shop With Free Shipping
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Covered States List */}
      <section className="py-12 md:py-16" ref={statesListRef}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            States With Professional Installation
          </h2>
          {coveredStates.length > 0 ? (
            <>
              <p className="text-lg text-gray-600 text-center mb-8">
                We currently offer professional installation in {coveredStates.length} state{coveredStates.length !== 1 ? 's' : ''}.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                {coveredStates.map((state) => (
                  <div
                    key={state.name}
                    className="bg-white rounded-xl p-5 shadow hover:shadow-md transition-shadow border border-gray-100"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{state.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{state.zip_count} zip codes</p>
                      </div>
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-1 rounded-full">
                        <Check className="w-3 h-3" />
                        Available
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-xl text-gray-600 mb-2">We&apos;re expanding our installation network</p>
              <p className="text-gray-500">Professional installation coming soon. In the meantime, enjoy free shipping to all 50 states with easy DIY installation.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
